import { useSetAtom, useAtomValue } from 'jotai'
import { dictionaryState, languageState } from '@repo/unicity'
import { RetryGuard } from '@repo/unicity/guard'
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'

// Supported languages for glic2027 (uppercase codes)
const SUPPORTED_LANGUAGES = ['EN', 'AR', 'DE', 'ES', 'FR', 'JP', 'KM', 'KR', 'TH', 'VN', 'ZH'] as const
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

// Default language
const DEFAULT_LANGUAGE: SupportedLanguage = 'EN'

// Dictionary API base URL
const DICTIONARY_API_BASE = 'https://member-calls2.unicity.com'
const DICTIONARY_WEB = 'glic2027'

// Fixed language to country code mapping for glic2027 (uppercase keys)
const LANGUAGE_TO_COUNTRY: Record<string, string> = {
    EN: 'USA',
    AR: 'ARE', // Arabic - UAE
    DE: 'DEU', // German - Germany
    ES: 'ESP', // Spanish - Spain
    FR: 'FRA', // French - France
    JP: 'JPN', // Japanese - Japan
    KM: 'KHM', // Khmer - Cambodia
    KR: 'KOR', // Korean - Korea
    TH: 'THA', // Thai - Thailand
    VN: 'VNM', // Vietnamese - Vietnam
    ZH: 'TWN', // Chinese - Taiwan
    CH: 'HKG' // Chinese - China
}

// Map language code to locale (dictionary key from API response)
const CODE_TO_LOCALE: Record<string, string> = {
    EN: 'en',
    AR: 'ar',
    DE: 'de',
    ES: 'es',
    FR: 'fr',
    JP: 'ja',
    KM: 'km',
    KR: 'ko',
    TH: 'th',
    VN: 'vi',
    ZH: 'zh',
    CH: 'cn'
}

/**
 * Hook to initialize and manage dictionary for glic2027
 * - Default language is 'EN' (USA)
 * - Supported: EN, AR, DE, ES, FR, JP, KM, KR, TH, VN, ZH
 * - Loads dictionary once on initialization
 * - Fetches new dictionary when language changes via changeLanguage()
 * - Caches dictionaries to avoid re-fetching
 */
export function useGlicDictionary() {
    const setDictionary = useSetAtom(dictionaryState)
    const setLanguage = useSetAtom(languageState)
    const currentLanguage = useAtomValue(languageState)

    const [isLoading, setIsLoading] = useState(false)
    const [loadedLanguages, setLoadedLanguages] = useState<Set<string>>(new Set())
    const [isInitialized, setIsInitialized] = useState(false)

    // Guard rail: a failed fetch returns null so the language never enters
    // loadedLanguages, and the isLoading toggle re-fires the effect → tight refetch
    // loop. Cap it at 3 consecutive failures per language.
    const failGuards = useRef(new Map<string, RetryGuard>())
    const guardFor = (lang: string) => {
        const existing = failGuards.current.get(lang)
        if (existing) return existing
        const g = new RetryGuard(3)
        failGuards.current.set(lang, g)
        return g
    }

    // Use fixed language to country code mapping
    const languageToCountryMap = LANGUAGE_TO_COUNTRY

    // Fetch dictionary for a specific language (expects uppercase langCode)
    const fetchDictionary = useCallback(
        async (langCode: string) => {
            const langUpper = langCode.toUpperCase()
            const countryCode = languageToCountryMap[langUpper]
            if (!countryCode) {
                console.warn(`No country code mapping for language: ${langUpper}`)
                return null
            }

            try {
                setIsLoading(true)
                const url = `${DICTIONARY_API_BASE}/hot/dictionary/v2/publish/${countryCode}/${DICTIONARY_WEB}`
                const response = await axios.get(url)
                return response.data
            } catch (error) {
                console.error(`Failed to fetch dictionary for ${langUpper}:`, error)
                return null
            } finally {
                setIsLoading(false)
            }
        },
        [languageToCountryMap]
    )

    // Set initial language (only once)
    // Priority: 1. URL query string (?lang=xx) -> 2. localStorage -> 3. default 'EN'
    useEffect(() => {
        if (isInitialized) return

        // 1. Check URL query string first (e.g., ?lang=en or ?lang=th)
        const urlParams = new URLSearchParams(window.location.search)
        const langFromUrl = urlParams.get('lang')?.toUpperCase()

        if (langFromUrl && SUPPORTED_LANGUAGES.includes(langFromUrl as SupportedLanguage)) {
            setLanguage(langFromUrl)
            localStorage.setItem('glic2027_language', langFromUrl)
            setIsInitialized(true)
            return
        }

        // 2. Check localStorage
        const savedLanguage = localStorage.getItem('glic2027_language')
        const savedUpper = savedLanguage?.toUpperCase()
        if (savedUpper && SUPPORTED_LANGUAGES.includes(savedUpper as SupportedLanguage)) {
            setLanguage(savedUpper)
        } else {
            // 3. Use default 'EN'
            setLanguage(DEFAULT_LANGUAGE)
            localStorage.setItem('glic2027_language', DEFAULT_LANGUAGE)
        }

        setIsInitialized(true)
    }, [setLanguage, isInitialized])

    // Fetch dictionary when language changes (only after initialization)
    useEffect(() => {
        // Wait for initialization to complete first
        if (!isInitialized) {
            return
        }

        const langUpper = currentLanguage.toUpperCase()

        // Skip if already loaded, currently loading, or this language has failed too
        // many times in a row (guard rail against the refetch loop).
        if (loadedLanguages.has(langUpper) || isLoading || guardFor(langUpper).exhausted) {
            return
        }

        // Fetch new dictionary
        const loadDictionary = async () => {
            const newDict = await fetchDictionary(langUpper)
            if (newDict) {
                // API response already has language keys (e.g. { "en": {...}, "th": {...} })
                // Merge directly into dictionary state
                setDictionary(prev => ({
                    ...prev,
                    ...newDict
                }))
                setLoadedLanguages(prev => new Set(prev).add(langUpper))
                guardFor(langUpper).succeed()
            } else {
                // fetchDictionary swallows the error and returns null — count it so a
                // persistently-failing language stops re-firing the effect.
                guardFor(langUpper).fail()
            }
        }

        loadDictionary()
    }, [currentLanguage, fetchDictionary, isLoading, loadedLanguages, setDictionary, isInitialized])

    // Change language function - loads dictionary first, then changes language
    const changeLanguage = useCallback(
        async (langCode: string) => {
            const normalizedLang = langCode.toUpperCase()
            if (!SUPPORTED_LANGUAGES.includes(normalizedLang as SupportedLanguage)) {
                console.warn(`Unsupported language: ${langCode}`)
                return
            }

            // Load dictionary first, then change language
            const newDict = await fetchDictionary(normalizedLang)
            if (newDict) {
                setDictionary(prev => ({
                    ...prev,
                    ...newDict
                }))
                setLoadedLanguages(prev => new Set(prev).add(normalizedLang))

                // Wait for React to process dictionary update before changing language
                await new Promise(resolve => setTimeout(resolve, 0))
            }

            // Change language after dictionary is loaded
            setLanguage(normalizedLang)
            localStorage.setItem('glic2027_language', normalizedLang)
        },
        [setLanguage, fetchDictionary, setDictionary]
    )

    return {
        currentLanguage,
        changeLanguage,
        isLoading,
        supportedLanguages: SUPPORTED_LANGUAGES,
        languageToCountryMap
    }
}

export default useGlicDictionary
